import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { ToastService } from '@pure/hive';
import { map, Observable, take } from 'rxjs';

import { EffectiveOrgService } from '../../data/services/effective-org.service';
import { OrgSwitcherService } from '../../page/page-wrapper/org-switcher/org-switcher.service';

/**
 * canActivate on the `/orgs/:orgUid` parent. Validates that the uid in the URL is in
 * the user's accessible org list and primes EffectiveOrgService so OrgHeadersInterceptor
 * can attach org headers. Redirects to `/errors` when the uid is missing or unknown.
 */
export const orgUidValidatorGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> | UrlTree => {
    const router = inject(Router);
    const orgSwitcherService = inject(OrgSwitcherService);
    const effectiveOrgService = inject(EffectiveOrgService);
    const toastService = inject(ToastService);

    const orgUid: string | undefined = route.params['orgUid'];
    if (!orgUid) return router.parseUrl('/errors');

    return orgSwitcherService.userOrgs$.pipe(
        take(1),
        map(orgs => {
            const match = orgs.find(o => o.uid === orgUid);
            if (!match) {
                toastService.addToast({
                    severity: 'major',
                    heading: 'Organization not found',
                    message:
                        'The organization you were trying to open is not accessible. You have been redirected to your default organization.',
                });
                return router.parseUrl('/');
            }
            effectiveOrgService.setCurrentOrg({
                id: match.id,
                uid: match.uid,
                name: match.name,
                category: match.category as string,
                opCompanyUid: match.opCompany.uid,
                opCompanyName: match.opCompany.name,
            });
            return true;
        }),
    );
};
